"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FullSpectrumSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [activeTab, setActiveTab] = useState("ai-answering");
  const tabs = [{
    value: "ai-answering",
    label: "AI Answering",
    heading: "Never miss a lead, even when you're off the clock.",
    description: "Our intelligent AI agents handle your incoming calls 24/7 with a professional, human-like touch. We ensure every potential client is greeted, qualified, and scheduled, turning missed opportunities into booked appointments while you focus on your craft.",
    pillars: ["24/7 automated call handling", "Instant lead qualification", "Seamless calendar integration", "Customized brand voice scripts"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/ai_answering_service.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/ai_answering_service.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/ai_answering_service.png-wvc-sizes",
      alt: "AI-driven communication interface for business growth"
    }
  }, {
    value: "web-design",
    label: "Web Design",
    heading: "High-conversion sites that look like premium brands.",
    description: "Your website is your digital storefront. We build high-performance, mobile-responsive sites designed to convert visitors into loyal customers. We combine sleek aesthetics with tactical layouts to ensure your business stands out in the local market.",
    pillars: ["Mobile-first responsive design", "Conversion-optimized landing pages", "Fast-loading technical architecture", "SEO-ready content structure"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/premium_web_design.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/premium_web_design.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/premium_web_design.webp-wvc-sizes",
      alt: "Modern professional website design on a laptop screen"
    }
  }, {
    value: "logo-creation",
    label: "Logo Creation",
    heading: "Visual identity that commands authority.",
    description: "A brand is more than a name; it's a reputation. We craft custom logos that capture the essence of your business, helping you project the professionalism and reliability that your clients expect from a top-tier local service provider.",
    pillars: ["Custom vector logo design", "Brand color palette development", "Typography and style guides", "Versatile assets for all media"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/brand_logo_creation.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/brand_logo_creation.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/brand_logo_creation.webp-wvc-sizes",
      alt: "Professional logo design process and brand assets"
    }
  }];
  return <section data-section-id="751" id="full-spectrum-solutions" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex items-center gap-3">
            
            
          </div>

          <h2 className="font-default font-bold text-foreground text-center leading-[1.05] ![margin:24px_0px_0px_0px]" style={{
          fontSize: "clamp(2.5rem, 4vw, 4rem)"
        }}>Complete business services.</h2>

          <p className="font-default text-foreground/80 mt-8 leading-relaxed text-center mx-auto" style={{
          fontSize: "1.25rem",
          maxWidth: "640px"
        }}>We are AI Guyz, providing the tactical services local businesses need to stop missing calls, look like premium brands, and put their business on autopilot.</p>
        </div>

        <div className="mt-20">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-border">
              <TabsList className="bg-transparent p-0 h-auto w-full flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible justify-start gap-0">
                {tabs.map((tab, i) => <TabsTrigger key={tab.value} value={tab.value} data-index={i} className="relative bg-transparent border-0 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 md:px-8 py-5 text-sm font-bold tracking-widest uppercase text-foreground/50 data-[state=active]:text-primary transition-colors duration-300 whitespace-nowrap first:pl-0">
                    {tab.label}
                    {activeTab === tab.value && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-primary transition-all duration-400" />}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {tabs.map((tab, i) => <TabsContent key={tab.value} value={tab.value} data-index={i} className="mt-16 focus-visible:outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-3 data-[state=active]:duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                  <div className="lg:col-span-6">
                    <h3 className="font-default font-bold text-foreground leading-[1.2]" style={{
                  fontSize: "clamp(1.75rem, 2.5vw, 2rem)"
                }}>
                      {tab.heading}
                    </h3>

                    <p className="font-default text-foreground/80 mt-6" style={{
                  fontSize: "1.125rem",
                  lineHeight: "1.65"
                }}>
                      {tab.description}
                    </p>

                    <ul className="mt-10 space-y-4">
                      {tab.pillars.map((pillar, idx) => <li key={idx} data-index={idx} className="flex items-start gap-4">
                          <span className="mt-2 inline-block h-2 w-2 shrink-0 bg-primary" aria-hidden="true" />
                          <span className="font-default text-base text-foreground/90">
                            {pillar}
                          </span>
                        </li>)}
                    </ul>

                    <Link to="#" className="group mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-opacity hover:opacity-80 text-primary">
                      <span className="border-b border-current pb-0.5">
                        Deploy this solution
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="relative overflow-hidden rounded-lg shadow-lg" style={{
                  aspectRatio: "4 / 5"
                }}>
                      <img src={tab.image.src} data-wvc-srcset={tab.image.srcset} data-wvc-sizes={tab.image.sizes} alt={tab.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}